import { appUrl } from '../../config/appUrl';
import { ArrowRight, Baby, Coffee, Laptop, Moon, Salad, Search, UtensilsCrossed } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { LandingAdaptiveBlock } from './types';
import { track } from './types';

const icons: Record<string, ReactNode> = {
  coffee: <Coffee />,
  work: <Laptop />,
  kids: <Baby />,
  healthy: <Salad />,
  dinner: <UtensilsCrossed />,
  nightlife: <Moon />,
};

const ROTATE_MS = 4500;

/**
 * Admin-managed home prompts (System settings → home adaptive blocks), shown
 * one at a time and rotating on their own. Hover or focus pauses the rotation.
 */
export function AdaptiveBlockBanner({ blocks, locale, city }: { blocks: LandingAdaptiveBlock[]; locale: 'th' | 'en'; city: string }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = blocks.length;
  useEffect(() => { if (index >= count) setIndex(0); }, [count, index]);
  useEffect(() => {
    if (count < 2 || paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => { if (!document.hidden) setIndex(current => (current + 1) % count); }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [count, paused]);
  if (!count) return null;

  return <section className="hx-workspace" aria-roledescription="carousel" aria-label="ค้นหาตามโอกาส" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
    <div className="hx-workspace__viewport" aria-live="polite">
      {blocks.map((block, i) => {
        const title = locale === 'en' ? block.title_en || block.title_th : block.title_th;
        const subtitle = locale === 'en' ? block.subtitle_en || block.subtitle_th : block.subtitle_th;
        return <a key={block.id} className={`hx-workspace__slide ${i === index ? 'is-active' : ''}`} aria-hidden={i !== index} tabIndex={i === index ? 0 : -1}
          href={appUrl(`/search/results?q=${encodeURIComponent(block.query)}`)}
          onClick={() => track('landing_adaptive_block_click', { block_id: block.id, city })}>
          <span className="hx-workspace__icon">{icons[block.icon] ?? <Search />}</span>
          <span className="hx-workspace__copy">
            <span className="hx-workspace__prompt">{locale === 'en' ? 'What to Beaw?' : 'เบียวไรดี??'}</span>
            <b>{title}</b>
            {subtitle && <small>{subtitle}</small>}
          </span>
          <span className="hx-workspace__arrow"><ArrowRight /></span>
        </a>;
      })}
    </div>
    {count > 1 && <div className="hx-workspace__dots" role="tablist" aria-label="เลือกหัวข้อ">
      {blocks.map((block, i) => <button type="button" role="tab" key={block.id} aria-selected={i === index} aria-label={locale === 'en' ? block.title_en : block.title_th} className={i === index ? 'is-active' : ''} onClick={() => setIndex(i)} />)}
    </div>}
  </section>;
}
